import { env } from "@/env";
import { fetchApi } from "@/lib/api-helpers";
import { z } from "zod";
import { cartResponseSchema } from "@/networking/carts.api";

export const languageSchema = z.object({
  id: z.number(),
  iso: z.string(),
  name: z.string(),
});

const stateSchema = z.object({
  isoCode: z.string(),
  name: z.string(),
  countryCode: z.string(),
});

export type State = z.infer<typeof stateSchema>;

export async function getStates(countryCode: string) {
  return fetchApi({
    endpoint: `${env.NEXT_PUBLIC_NEXT_API_URL}/countries?countryCode=${countryCode}`,
    method: "GET",
    outputSchema: z.array(stateSchema),
  });
}

export const addressSchema = z.object({
  address: z.object({
    first_name: z.string(),
    last_name: z.string(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    country_code: z.string(),
  }),
  type: z.enum(["ship_to", "bill_to", "both"]),
  use_shipping_address_for_billing: z.boolean().optional(),
});

export type Address = z.infer<typeof addressSchema>;

const countrySchema = z.object({
  id: z.number(),
  iso: z.string(),
  name: z.string(),
});

export const countriesAndLanguagesSchema = z.object({
  languages: z.array(languageSchema),
  countries: z.array(countrySchema),
});

export async function fetchCountriesAndLanguages(cartToken: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/cart_info`,
    method: "GET",
    outputSchema: countriesAndLanguagesSchema,
  });
}

export type CountriesAndLanguages = z.infer<typeof countriesAndLanguagesSchema>;

export async function updateCartCountry(
  cartToken: string,
  country: { country_code: string }
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/update_country`,
    method: "PATCH",
    input: country,
    outputSchema: cartResponseSchema,
  });
}

export async function updateAddress(address: Address, cartToken: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/address`,
    method: "POST",
    input: address,
    outputSchema: cartResponseSchema,
  });
}

export interface AddressTranslations {
  firstNameRequired?: string;
  lastNameRequired?: string;
  addressLine1Required?: string;
  cityRequired?: string;
  stateRequired?: string;
  postalCodeMinCharacters?: string;
  countryRequired?: string;
}

export const createBillingAddressSchema = (translations?: AddressTranslations) =>
  z.object({
    address1: z.string().min(1, translations?.addressLine1Required ?? "Address line 1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, translations?.cityRequired ?? "City is required"),
    state: z.string().min(1, translations?.stateRequired ?? "State is required"),
    postal_code: z.string().min(4, translations?.postalCodeMinCharacters ?? "Postal code must be at least 4 characters"),
    country: z.string().min(1, translations?.countryRequired ?? "Country is required"),
  });

// default for backward compatibility
export const billingAddressSchema = createBillingAddressSchema();

export const shippingAddressSchema = (
  provinceRequired: boolean,
  translations?: AddressTranslations
) =>
  z.object({
    first_name: z.string().min(1, translations?.firstNameRequired ?? "First name is required"),
    last_name: z.string().min(1, translations?.lastNameRequired ?? "Last name is required"),
    address1: z.string().min(1, translations?.addressLine1Required ?? "Address line 1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, translations?.cityRequired ?? "City is required"),
    state: provinceRequired
      ? z.string().min(1, translations?.stateRequired ?? "State is required")
      : z.string().optional(),
    postal_code: z.string().min(4, translations?.postalCodeMinCharacters ?? "Postal code must be at least 4 characters"),
    country: z.string().min(1, translations?.countryRequired ?? "Country is required"),
    country_code: z.string(),
  });

export const createCustomerAddressSchema = (translations?: AddressTranslations) =>
  z.object({
    first_name: z.string().min(1, translations?.firstNameRequired ?? "First name is required"),
    last_name: z.string().min(1, translations?.lastNameRequired ?? "Last name is required"),
    address1: z.string().min(1, translations?.addressLine1Required ?? "Address line 1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, translations?.cityRequired ?? "City is required"),
    state: z.string().min(1, translations?.stateRequired ?? "State is required"),
    postal_code: z.string().min(4, translations?.postalCodeMinCharacters ?? "Postal code must be at least 4 characters"),
    country: z.string().min(1, translations?.countryRequired ?? "Country is required"),
    default: z.boolean().optional(),
  });

// default for backward compatibility
export const customerAddressSchema = createCustomerAddressSchema();

export type AddressFormData = z.infer<typeof customerAddressSchema>;

const customerMetaSchema = z.object({
  request_id: z.string(),
  timestamp: z.string(),
  pagination: z
    .object({
      current_page: z.number(),
      total_pages: z.number(),
      total_count: z.number(),
      per_page: z.number(),
    })
    .optional(),
});

const addressObjectSchema = z.object({
  id: z.number(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  country_code: z.string().nullable(),
  default: z.boolean(),
  name: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  postal_code: z.string(),
  state: z.string(),
  subdivision_code: z.string().nullable(),
});

const customerAddressElementSchema = addressObjectSchema;

const customerAddressResponseSchema = z.object({
  fluid_pay_account: z.object({
    id: z.number(),
    default_address_id: z.number().nullable(),
  }),
  addresses: z.array(customerAddressElementSchema),
  meta: customerMetaSchema,
});

export type CustomerAddressResponse = z.infer<
  typeof customerAddressResponseSchema
>;

export type CustomerAddress = z.infer<typeof customerAddressElementSchema>;

const createAddressBodySchema = z.object({
  address: z.object({
    address1: z.string(),
    address2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    country_code: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    default: z.boolean(),
  }),
});

export type CreateAddressBody = z.infer<typeof createAddressBodySchema>;

export const createCustomerAddressResponseSchema = z.object({
  address: addressObjectSchema,
  fluid_pay_account: z.object({
    id: z.number(),
  }),
});

export type CreateCustomerAddressResponse = z.infer<
  typeof createCustomerAddressResponseSchema
>;

export async function fetchCustomerAddresses(jwt: string) {
  const query = new URLSearchParams({
    jwt,
    page: "1",
    per_page: "100",
  });
  return fetchApi({
    endpoint: `/fluid_pay/addresses?${query}`,
    method: "GET",
    outputSchema: customerAddressResponseSchema,
  });
}

export async function addAddressToCart(
  cartToken: string,
  type: string,
  jwt: string,
  address_id: string
) {
  const query = new URLSearchParams({
    type,
    jwt,
    address_id,
  }).toString();
  return fetchApi({
    endpoint: `/carts/${cartToken}/address?${query}`,
    method: "POST",
    outputSchema: cartResponseSchema,
  });
}

export async function createCustomerAddress(
  jwt: string,
  body: CreateAddressBody
) {
  const query = new URLSearchParams({
    jwt,
  });
  return fetchApi({
    endpoint: `/fluid_pay/create_address?${query}`,
    method: "POST",
    input: body,
    inputSchema: createAddressBodySchema,
    outputSchema: createCustomerAddressResponseSchema,
  });
}

export async function deleteCustomerAddress(address_id: string, jwt: string) {
  return fetchApi({
    endpoint: `/fluid_pay/delete_address/${address_id}?jwt=${jwt}`,
    method: "DELETE",
    outputSchema: z.object({}),
  });
}

export async function updateCustomerAddress(
  address_id: string,
  jwt: string,
  body: CreateAddressBody
) {
  return fetchApi({
    endpoint: `/fluid_pay/update_address/${address_id}?jwt=${jwt}`,
    method: "PATCH",
    input: body,
    inputSchema: createAddressBodySchema,
    outputSchema: createCustomerAddressResponseSchema,
  });
}
